import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { RouteType } from "src/app/models/enum";
import { UserFormRequestJSON } from "src/app/models/models";
import {
	SuccessSnackbarComponent,
	SuccessSnackbarData,
} from "src/app/shared/components/cbta-snackbar/success-snackbar.component";
import { HasUnsavedFormChanges } from "src/app/shared/interfaces/has-unsaved-form-changes";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { assignableUserRoles, formatUserRoleLabel, mergeAssignableUserRoles } from "src/app/shared/utils/user-role.utils";

/** rxjs helpers for debounce and teardown */
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { catchError, debounceTime, distinctUntilChanged, filter, of, switchMap, tap } from "rxjs";

@Component({
	selector: "app-create-user",
	templateUrl: "./create-user.component.html",
	styleUrl: "./create-user.component.scss",
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUserComponent implements OnInit, HasUnsavedFormChanges {
	createNewUser!: UntypedFormGroup;
	userId: string = "";
	userEmail: string = "";
	formSuccess: boolean = false;

	private snackBar = inject(MatSnackBar);
	private destroyRef = inject(DestroyRef);
	sessionMasterData = this.sharedService.sessionMasterData;

	/** tiny state for UI feedback */
	loadingEmailLookup = false;
	workdayHitName: string | null = null;

	constructor(
		private fb: UntypedFormBuilder,
		private httpService: HttpService,
		private loadingService: LoaderService,
		public sharedService: SharedService,
		private router: Router,
	) {
		this.initCreateNewUser();
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.USER_MANAGEMENT);
		this.setupNameUppercase();
		this.setupEmailAutoLookup(); // start listening for email typing
	}

	initCreateNewUser(): void {
		this.createNewUser = this.fb.group({
			name: ["", [Validators.required, Validators.maxLength(100)]],
			email: ["", [Validators.required, Validators.email, this.customUserEmailValidator]],
			role: [[], [Validators.required]],
			aoc: ["", [Validators.required]],
			external: ["", [Validators.required]],
		});
	}

	get name() {
		return this.createNewUser.get("name")!;
	}

	get email() {
		return this.createNewUser.get("email")!;
	}

	get role() {
		return this.createNewUser.get("role")!;
	}

	get aoc() {
		return this.createNewUser.get("aoc")!;
	}

	get aocOptions() {
		return this.sessionMasterData?.aocs;
	}

	get rolesOptions() {
		return assignableUserRoles(this.sessionMasterData?.roles);
	}

	get isExternal() {
		return this.createNewUser.get("external")!;
	}

	removeRole(roleToRemove: string): void {
		const currentRoles = this.role.value || [];
		const updatedRoles = currentRoles.filter((role: string) => role !== roleToRemove);
		this.role.setValue(updatedRoles);
	}

	getRoleDisplayName(role: string): string {
		return formatUserRoleLabel(role);
	}

	private setupNameUppercase(): void {
		this.name.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
			const upper = String(value ?? "").toUpperCase();
			if (value !== upper) {
				this.name.setValue(upper, { emitEvent: false });
			}
		});
	}

	private normalizeUserName(value: unknown): string {
		return String(value ?? "").trim().toUpperCase();
	}

	customUserEmailValidator(control: AbstractControl): ValidationErrors | null {
		if (!control.value || control.value.trim() === "") {
			return null;
		}

		const email = control.value.toLowerCase();
		const allowedDomain = "@airasia.com";

		if (!email.endsWith(allowedDomain)) {
			return {
				customUserEmail: {
					actualDomain: email.split("@")[1] || "",
					requiredDomain: allowedDomain.substring(1),
				},
			};
		}

		return null;
	}

	/** Debounced lookup that auto-populates name and toggles enable/disable */
	private setupEmailAutoLookup(): void {
		const emailCtrl = this.email;

		emailCtrl
			.valueChanges!.pipe(
				debounceTime(500),
				distinctUntilChanged(),
				tap(() => {
					this.workdayHitName = null;
				}),
				filter(() => !!emailCtrl.value && emailCtrl.valid), // only valid emails (incl domain) trigger API
				tap(() => {
					this.loadingEmailLookup = true;
				}),

				switchMap((raw) =>
					this.httpService.getUserInfoWorkday(String(raw).trim().toLowerCase()).pipe(
						catchError(() => of(null)), // treat error as not found
					),
				),

				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((res: any) => {
				this.loadingEmailLookup = false;

				const foundName = res?.data?.name ?? null;

				if (foundName) {
					const normalizedName = this.normalizeUserName(foundName);
					this.workdayHitName = normalizedName;
					this.name.setValue(normalizedName, { emitEvent: false });
					this.name.disable({ emitEvent: false }); // lock when found
				} else {
					this.workdayHitName = null;

					if (this.name.disabled) {
						this.name.enable({ emitEvent: true });
					}

					// clear the field
					this.name.reset("", { emitEvent: true });
				}
			});
	}

	onSubmit(): void {
		this.createNewUser.updateValueAndValidity();
		this.createNewUser.markAllAsTouched();

		if (this.createNewUser.errors) {
			return;
		}

		if (this.createNewUser.valid) {
			const formValue = this.createNewUser.getRawValue();

			const userData: UserFormRequestJSON = {
				name: this.normalizeUserName(formValue.name),
				email: formValue.email,
				roles: mergeAssignableUserRoles(formValue.role, []),
				aoc: formValue.aoc,
				is_external: formValue.external,
			};

			this.loadingService.setLoading(true);

			this.httpService.addUser(userData).subscribe({
				next: this.onSuccessFormSubmit.bind(this),
			});
		}
	}

	hasUnsavedFormChanges(): boolean {
		return !this.formSuccess && this.createNewUser?.dirty === true;
	}

	goBack(): void {
		void this.router.navigate([RouteType.USER_MANAGEMENT]);
	}

	onSuccessFormSubmit(resp: any) {
		this.formSuccess = true;

		const userName = this.createNewUser.get("name")?.value;

		this.snackBar.openFromComponent(SuccessSnackbarComponent, {
			data: {
				userName: userName,
				action: "create",
			} as SuccessSnackbarData,
			duration: 4000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["success-snackbar"],
		});

		setTimeout(() => {
			this.router.navigate([RouteType.USER_MANAGEMENT]);
		}, 2000);
		this.loadingService.setLoading(false);
	}
}
